import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { specificUserEmails } from "components/RequireSpecificEmails/requireSpecificEmails";
import { SECTION_LABELS } from "./useDashboardSettings";

const ACCENT = "#26890d";

// Editing these dashboard-wide settings is restricted to the same allowlist
// used for other admin-only tooling (see RequireSpecificEmails) — everyone
// can view the dashboard, but only allowlisted emails can change what's
// visible or the Deloitte overage rate for everyone else.
const useCanEditDashboardSettings = () => {
  const userEmail = useSelector((state) => state.auth?.userAccount?.username?.toLowerCase());
  return specificUserEmails.includes(userEmail);
};

const DashboardSettingsMenu = ({ visibleSections, onToggleSection, deloitteOverageRate, onUpdateDeloitteOverageRate, onReset }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rateInput, setRateInput] = useState(String(deloitteOverageRate ?? ""));
  const open = Boolean(anchorEl);
  const canEdit = useCanEditDashboardSettings();

  // Only allowlisted emails (REACT_APP_SPECIFIC_USER_EMAILS) can see this
  // control at all — everyone else gets no settings button on the dashboard.
  if (!canEdit) return null;

  const handleOpen = (e) => {
    setRateInput(String(deloitteOverageRate ?? ""));
    setAnchorEl(e.currentTarget);
  };

  const commitRate = () => {
    const parsed = Number.parseFloat(rateInput);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      onUpdateDeloitteOverageRate(parsed);
    } else {
      setRateInput(String(deloitteOverageRate ?? ""));
    }
  };

  return (
    <>
      <Tooltip title="Customize dashboard settings" placement="top" arrow>
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{
            color: open ? ACCENT : "#555",
            border: `1px solid ${open ? ACCENT : "#ccc"}`,
            borderRadius: "16px",
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: "#f2f8e6", borderColor: ACCENT, color: ACCENT },
          }}
        >
          <SettingsOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "12px", border: "1px solid #e8e8e8", boxShadow: "0 12px 32px rgba(0,0,0,0.14)", mt: 1 } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1a1a1a" }}>
              Dashboard Settings
            </Typography>
            <Typography
              onClick={onReset}
              sx={{
                fontSize: "0.72rem",
                color: "#888",
                cursor: "pointer",
                "&:hover": { color: ACCENT, textDecoration: "underline" },
              }}
            >
              Reset
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.72rem", color: "#999", mb: 1.5 }}>
            Applies to everyone. Choose which sections appear on this dashboard — changes apply immediately.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {Object.entries(SECTION_LABELS).map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={visibleSections[key] ?? true}
                    onChange={() => onToggleSection(key)}
                    size="small"
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": { color: ACCENT },
                      py: 0.4,
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: "0.82rem", color: "#333" }}>{label}</Typography>}
                sx={{ ml: 0, mr: 0 }}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#555", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Deloitte Overage Rate
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "#999", mb: 1 }}>
            Negotiated $/AIC rate applied to overage billing across the dashboard.
          </Typography>
          <TextField
            value={rateInput}
            onChange={(e) => setRateInput(e.target.value)}
            onBlur={commitRate}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            size="small"
            type="number"
            inputProps={{ step: "0.0001", min: 0 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">/AIC</InputAdornment>,
            }}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.82rem",
                "&.Mui-focused fieldset": { borderColor: ACCENT },
              },
            }}
          />
        </Box>
      </Popover>
    </>
  );
};

export default DashboardSettingsMenu;
